import { useSyncExternalStore } from 'react';

/**
 * Registry of mounted permission frames, keyed by componentId.
 *
 * One permission often wraps several disconnected regions — `order-lc-fields`, for
 * example, is wrapped at ten call sites across the Order Info / Addl Info / Invoicing
 * views. A frame drawn around one of them reads as "the permission stops here", so we
 * track which componentIds are mounted (and how many times) and which one is currently
 * being pointed at, letting every co-scoped frame highlight together.
 *
 * This is a module-level external store rather than a React context on purpose: frames
 * render inside drawers, portals, and test trees that don't share a common provider.
 */

type Listener = () => void;

const frameCounts = new Map<string, number>();
let activeComponentId: string | null = null;
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((listener) => listener());

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Register a mounted frame. Returns the matching unregister callback. */
export const registerFrame = (componentId: string): (() => void) => {
  frameCounts.set(componentId, (frameCounts.get(componentId) ?? 0) + 1);
  emit();
  return () => {
    const next = (frameCounts.get(componentId) ?? 1) - 1;
    if (next <= 0) {
      frameCounts.delete(componentId);
    } else {
      frameCounts.set(componentId, next);
    }
    emit();
  };
};

export const setActiveComponentId = (componentId: string): void => {
  if (activeComponentId === componentId) return;
  activeComponentId = componentId;
  emit();
};

/**
 * Clear the active component, but only if it's still `componentId`. Guards against a
 * closing popover stomping a highlight the pointer has since moved to.
 */
export const clearActiveComponentId = (componentId: string): void => {
  if (activeComponentId !== componentId) return;
  activeComponentId = null;
  emit();
};

/** How many frames for this componentId are mounted right now. */
export const useFrameCount = (componentId: string): number =>
  useSyncExternalStore(subscribe, () => frameCounts.get(componentId) ?? 0);

/** Whether this componentId is the one being pointed at — here or at a sibling frame. */
export const useIsActiveComponent = (componentId: string): boolean =>
  useSyncExternalStore(subscribe, () => activeComponentId === componentId);

/** Test-only reset so module state doesn't leak between cases. */
export const __resetFrameRegistry = (): void => {
  frameCounts.clear();
  activeComponentId = null;
  emit();
};
